import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { Clock, IdGenerator, Logger } from '../ports'
import { extractMentionIds } from '../protocol/mentionText'
import {
  Attachment,
  Chat,
  ChatMessage,
  FailedMessage,
  MessageReceivedEvent,
  SendingMessage,
} from '../protocol/types'
import { describeChatError, fold, isErr } from '../result'
import { ChatApi } from '../transport/api'
import { ContactsCache } from './ContactsCache'
import { LoadStatus } from './Inbox.store'
import { mergeMessages } from './merge'

const PAGE_SIZE = 50

export class ChatLogStore {
  public chat: Chat
  public messages: ChatMessage[] = []
  public status: LoadStatus = 'idle'
  public isRemovedFromChat = false
  public hasOlder = true
  public isLoadingOlder = false
  // Set by the session, never inferred: cached stores outlive their screens,
  // so existing does not mean the user is looking at this conversation.
  public isActive = false

  public constructor(
    chat: Chat,
    private readonly userId: string,
    private readonly api: ChatApi,
    public readonly contacts: ContactsCache,
    private readonly logger: Logger,
    private readonly clock: Clock,
    private readonly newId: IdGenerator,
    private readonly onReadStateChanged?: () => void,
  ) {
    this.chat = chat
    makeObservable(this, {
      chat: observable,
      messages: observable.ref,
      status: observable,
      isRemovedFromChat: observable,
      hasOlder: observable,
      isLoadingOlder: observable,
      sortedMessages: computed,
      onChatUpdated: action,
    })
  }

  public get sortedMessages(): ChatMessage[] {
    return this.messages
  }

  public async open(): Promise<void> {
    runInAction(() => {
      if (this.status !== 'ready') {
        this.status = 'loading'
      }
    })

    this.contacts.ensureLoaded(this.chat.memberIds)

    const result = await this.api.getMessagesByChannel(this.chat.id, {
      limit: PAGE_SIZE,
    })
    fold(
      result,
      messages => {
        this.absorb(messages)
        runInAction(() => {
          this.status = 'ready'
          if (messages.length < PAGE_SIZE) {
            this.hasOlder = false
          }
        })
        this.markRead()
      },
      error => {
        this.logger.warn(
          `messages ${this.chat.id}: ${describeChatError(error)}`,
        )
        runInAction(() => {
          if (this.status !== 'ready') {
            this.status = 'error'
          }
        })
      },
    )
  }

  // The cursor is the createdAt of the oldest *sent* message — sending/failed
  // ones carry local clocks. A page shorter than PAGE_SIZE means history is
  // exhausted; a full page that yields nothing new after merge means the
  // server ignored the params (old server returning everything) — without
  // that rule every scroll-to-top would refetch the full history forever.
  public async loadOlder(): Promise<void> {
    if (!this.hasOlder || this.isLoadingOlder) {
      return
    }
    const oldestSent = this.messages.find(message => message.state === 'sent')
    if (!oldestSent || oldestSent.state !== 'sent') {
      return
    }

    runInAction(() => {
      this.isLoadingOlder = true
    })

    const result = await this.api.getMessagesByChannel(this.chat.id, {
      limit: PAGE_SIZE,
      before: oldestSent.createdAt,
    })
    fold(
      result,
      page => {
        const countBefore = this.messages.length
        this.absorb(page)
        const gained = this.messages.length - countBefore
        runInAction(() => {
          if (page.length < PAGE_SIZE || gained === 0) {
            this.hasOlder = false
          }
          this.isLoadingOlder = false
        })
      },
      error => {
        this.logger.warn(
          `older messages ${this.chat.id}: ${describeChatError(error)}`,
        )
        runInAction(() => {
          this.isLoadingOlder = false
        })
      },
    )
  }

  // Every cached log absorbs (projection — keeps history fresh for re-open),
  // but only the active one may declare the message read: marking read is a
  // statement of user attention, and an inactive store has none.
  public onMessageReceived = ({ message }: MessageReceivedEvent) => {
    if (message.channelId !== this.chat.id) {
      return
    }

    this.absorb([message])
    if (this.isActive && message.authorId !== this.userId) {
      this.markRead()
    }
  }

  public sendMessage(
    text: string,
    attachments: readonly Attachment[] = [],
  ): void {
    const trimmed = text.trim()
    if (!trimmed && !attachments.length) {
      return
    }

    const sending: SendingMessage = {
      state: 'sending',
      fakeId: this.newId(),
      channelId: this.chat.id,
      authorId: this.userId,
      createdAt: this.clock(),
      text: trimmed,
      attachments,
    }
    this.absorb([sending])
    this.post(sending)
  }

  // Retry is a lifecycle transition owned by this store, not a merge: merge
  // ranks failed above sending so a stale optimistic copy can never revive a
  // failure, which means the failed message is replaced in place here.
  public retry(fakeId: string): void {
    const failed = this.messages.find(
      message => message.state === 'failed' && message.fakeId === fakeId,
    )
    if (!failed || failed.state !== 'failed') {
      return
    }

    const sending: SendingMessage = {
      state: 'sending',
      fakeId: failed.fakeId,
      channelId: failed.channelId,
      authorId: failed.authorId,
      createdAt: failed.createdAt,
      text: failed.text,
      attachments: failed.attachments,
    }
    runInAction(() => {
      this.messages = this.messages.map(message =>
        message !== failed ? message : sending,
      )
    })
    this.post(sending)
  }

  // Takes over messages composed in a draft before the channel existed:
  // rehomes them onto this channel, posts the ones still sending, and keeps
  // failed ones failed so the regular retry affordance applies.
  public adopt(pending: readonly (SendingMessage | FailedMessage)[]): void {
    const rehomed = pending.map(message => ({
      ...message,
      channelId: this.chat.id,
    }))
    this.absorb(rehomed)
    rehomed.forEach(message => {
      if (message.state === 'sending') {
        this.post(message)
      }
    })
  }

  private async post(sending: SendingMessage): Promise<void> {
    const result = await this.api.sendMessage(
      sending.channelId,
      sending.text,
      sending.authorId,
      sending.fakeId,
      sending.attachments,
    )
    fold(
      result,
      id => {
        this.absorb([{ ...sending, state: 'sent', id }])
      },
      error => {
        this.logger.warn(`send ${this.chat.id}: ${describeChatError(error)}`)
        this.absorb([
          { ...sending, state: 'failed', reason: describeChatError(error) },
        ])
      },
    )
  }

  public onChatUpdated = (chat: Chat) => {
    if (chat.id !== this.chat.id) {
      return
    }

    if (chat.memberIds.includes(this.userId)) {
      this.chat = chat
    } else {
      this.isRemovedFromChat = true
    }
  }

  private absorb(incoming: readonly ChatMessage[]) {
    const merged = mergeMessages(this.messages, incoming)

    const mentionIds = incoming.flatMap(message =>
      message.text ? extractMentionIds(message.text) : [],
    )
    const authorIds = incoming.map(message => message.authorId)
    this.contacts.ensureLoaded([...authorIds, ...mentionIds])

    runInAction(() => {
      this.messages = merged
    })
  }

  private async markRead(): Promise<void> {
    const lastIncoming = [...this.messages]
      .reverse()
      .find(
        message => message.state === 'sent' && message.authorId !== this.userId,
      )
    if (!lastIncoming || lastIncoming.state !== 'sent') {
      return
    }

    const result = await this.api.setLastReadMessage(
      this.chat.id,
      lastIncoming.id,
      this.userId,
    )
    if (isErr(result)) {
      this.logger.warn(`mark read: ${describeChatError(result.error)}`)
      return
    }
    this.onReadStateChanged?.()
  }
}
